import type { MechanicDefinition } from "./types";

// ====== WEATHER TABLES ======

const WEATHER_TABLES: Record<string, Record<string, { weight: number; conditions: string[] }>> = {
  temperate: {
    spring: { weight: 1, conditions: ["clear:35", "cloudy:25", "rain:20", "fog:10", "storm:5", "wind:5"] },
    summer: { weight: 1, conditions: ["clear:45", "cloudy:20", "rain:15", "storm:10", "wind:10"] },
    autumn: { weight: 1, conditions: ["cloudy:30", "clear:25", "rain:20", "fog:15", "wind:10"] },
    winter: { weight: 1, conditions: ["cloudy:30", "snow:25", "clear:20", "fog:10", "storm:10", "wind:5"] },
  },
  tropical: {
    spring: { weight: 1, conditions: ["clear:30", "rain:30", "storm:20", "cloudy:15", "fog:5"] },
    summer: { weight: 1, conditions: ["rain:35", "storm:25", "clear:20", "cloudy:15", "fog:5"] },
    autumn: { weight: 1, conditions: ["rain:30", "clear:25", "storm:20", "cloudy:15", "fog:10"] },
    winter: { weight: 1, conditions: ["clear:35", "cloudy:25", "rain:20", "storm:10", "fog:10"] },
  },
  arctic: {
    spring: { weight: 1, conditions: ["cloudy:30", "snow:25", "clear:20", "wind:15", "blizzard:10"] },
    summer: { weight: 1, conditions: ["clear:35", "cloudy:25", "rain:15", "fog:15", "wind:10"] },
    autumn: { weight: 1, conditions: ["cloudy:30", "snow:25", "wind:20", "clear:15", "blizzard:10"] },
    winter: { weight: 1, conditions: ["snow:30", "blizzard:25", "cloudy:20", "wind:15", "clear:10"] },
  },
  desert: {
    spring: { weight: 1, conditions: ["clear:50", "wind:20", "sandstorm:15", "cloudy:10", "rain:5"] },
    summer: { weight: 1, conditions: ["clear:55", "wind:20", "sandstorm:15", "cloudy:10"] },
    autumn: { weight: 1, conditions: ["clear:45", "wind:25", "cloudy:15", "sandstorm:10", "rain:5"] },
    winter: { weight: 1, conditions: ["clear:40", "cloudy:25", "wind:15", "fog:10", "rain:10"] },
  },
  alien: {
    spring: { weight: 1, conditions: ["clear:25", "acid_rain:20", "spore_fog:20", "ion_storm:15", "calm:10", "aurora:10"] },
    summer: { weight: 1, conditions: ["clear:25", "ion_storm:25", "acid_rain:15", "aurora:15", "spore_fog:10", "calm:10"] },
    autumn: { weight: 1, conditions: ["spore_fog:25", "clear:20", "acid_rain:20", "calm:15", "ion_storm:10", "aurora:10"] },
    winter: { weight: 1, conditions: ["calm:25", "spore_fog:20", "clear:20", "ion_storm:15", "acid_rain:10", "aurora:10"] },
  },
  varied: {
    spring: { weight: 1, conditions: ["clear:30", "cloudy:25", "rain:20", "fog:10", "wind:10", "storm:5"] },
    summer: { weight: 1, conditions: ["clear:40", "cloudy:20", "rain:15", "storm:10", "wind:10", "fog:5"] },
    autumn: { weight: 1, conditions: ["cloudy:30", "clear:20", "rain:20", "fog:15", "wind:10", "storm:5"] },
    winter: { weight: 1, conditions: ["cloudy:25", "snow:25", "clear:20", "wind:15", "storm:10", "fog:5"] },
  },
};

const SEASON_DAY_LENGTH: Record<string, { dawn: number; dusk: number }> = {
  spring: { dawn: 6, dusk: 19 },
  summer: { dawn: 4, dusk: 21 },
  autumn: { dawn: 6, dusk: 18 },
  winter: { dawn: 7, dusk: 17 },
};

const BASE_TEMP: Record<string, Record<string, { day: number; night: number }>> = {
  temperate: { spring: { day: 18, night: 8 }, summer: { day: 28, night: 16 }, autumn: { day: 14, night: 5 }, winter: { day: 3, night: -5 } },
  tropical: { spring: { day: 32, night: 24 }, summer: { day: 36, night: 26 }, autumn: { day: 30, night: 22 }, winter: { day: 28, night: 20 } },
  arctic: { spring: { day: -2, night: -15 }, summer: { day: 8, night: -2 }, autumn: { day: -5, night: -18 }, winter: { day: -20, night: -35 } },
  desert: { spring: { day: 35, night: 12 }, summer: { day: 45, night: 20 }, autumn: { day: 32, night: 10 }, winter: { day: 22, night: 2 } },
  alien: { spring: { day: 20, night: 5 }, summer: { day: 25, night: 10 }, autumn: { day: 15, night: 0 }, winter: { day: 5, night: -10 } },
  varied: { spring: { day: 18, night: 8 }, summer: { day: 28, night: 16 }, autumn: { day: 14, night: 5 }, winter: { day: 3, night: -5 } },
};

const WEATHER_TEMP_MOD: Record<string, number> = {
  clear: 2, cloudy: -2, rain: -4, storm: -6, snow: -8, blizzard: -15, fog: -1, wind: -3, sandstorm: 5,
  acid_rain: -3, spore_fog: -1, ion_storm: -5, calm: 0, aurora: -2,
};

export const timePassage: MechanicDefinition = {
  id: "time_passage",
  name: "Time passage",
  description: "Game clock with day/night cycle, weather, seasons, temperature, and optional turn-flow rules for multiplayer or structured play.",
  icon: "Clock",
  category: "systems",
  auditPasses: 1,
  aiHints: {
    purpose: "Core game clock with day/night, weather, seasons, temperature, and optional turn flow for multiplayer or structured play.",
    whenToUse: "Most worlds benefit from this. Essential for survival, travel, schedule-based gameplay, and any world that needs explicit turn order.",
    synergies: ["survival", "region_movement", "encounter_tables", "resting"],
    conflicts: [],
    prerequisites: [],
    tags: ["time", "weather", "day-night", "seasons", "temperature", "environment", "clock", "contextual", "turns", "initiative", "multiplayer"],
    complexity: 3,
    exampleConfig: {
      mode: "turns",
      hours_per_turn: 1,
      start_hour: 8,
      weather_enabled: true,
      climate: "temperate",
      seasons_enabled: true,
      temperature_tracking: true,
      contextual_environment: false,
      turn_mode: "free_form",
      phases_per_turn: false,
      mandatory_dice: false,
      time_limit_seconds: 0,
    },
    genrePresets: {
      "survival": { mode: "turns", hours_per_turn: 1, weather_enabled: true, climate: "arctic", seasons_enabled: true, temperature_tracking: true },
      "urban": { mode: "turns", hours_per_turn: 1, weather_enabled: true, climate: "temperate", seasons_enabled: false, temperature_tracking: false },
      "sci_fi": { mode: "hybrid", hours_per_turn: 2, weather_enabled: true, climate: "alien", seasons_enabled: false, temperature_tracking: true },
      "narrative": { contextual_environment: true },
      "historical": { contextual_environment: true },
    },
    stateVariables: ["game_hour", "game_day", "day_phase", "weather_state", "current_weather", "current_season", "current_temperature", "environment_description", "calendar_date", "turn_order", "current_turn_player", "current_turn_index", "_turn_mode", "_current_phase", "_last_time_advance"],
  },
  configSchema: [
    {
      key: "contextual_environment",
      label: "Contextual Mode (AI-Declared)",
      type: "boolean",
      default: false,
      description: "Let the AI declare time, weather, and date narratively instead of calculating them algorithmically. Simpler and more flexible — no hour/temperature math.",
    },
    {
      key: "mode",
      label: "Time Mode",
      type: "select",
      options: [
        { value: "turns", label: "Turn-based (advances per action)" },
        { value: "realtime", label: "Real-time mapped (wall clock)" },
        { value: "hybrid", label: "Hybrid (turns + idle ticking)" },
      ],
      default: "turns",
      description: "How the game clock advances",
      condition: { key: "contextual_environment", value: false },
    },
    {
      key: "hours_per_turn",
      label: "Hours per Turn",
      type: "number",
      default: 1,
      description: "In-game hours that pass per player turn",
      condition: { key: "contextual_environment", value: false },
    },
    {
      key: "start_hour",
      label: "Starting Hour (0-23)",
      type: "number",
      default: 8,
      description: "What hour the adventure begins",
      condition: { key: "contextual_environment", value: false },
    },
    {
      key: "weather_enabled",
      label: "Dynamic Weather",
      type: "boolean",
      default: true,
      description: "Algorithmic weather transitions based on climate and season",
      condition: { key: "contextual_environment", value: false },
    },
    {
      key: "climate",
      label: "Climate",
      type: "select",
      options: [
        { value: "temperate", label: "Temperate" },
        { value: "tropical", label: "Tropical" },
        { value: "arctic", label: "Arctic" },
        { value: "desert", label: "Desert" },
        { value: "alien", label: "Alien" },
        { value: "varied", label: "Varied (region-dependent)" },
      ],
      default: "temperate",
      condition: { key: "contextual_environment", value: false },
    },
    {
      key: "seasons_enabled",
      label: "Seasons",
      type: "boolean",
      default: true,
      description: "Track seasons that affect weather, day length, and temperature",
      condition: { key: "contextual_environment", value: false },
    },
    {
      key: "days_per_season",
      label: "Days per Season",
      type: "number",
      default: 30,
      description: "How many in-game days before the season changes",
      condition: { key: "contextual_environment", value: false },
    },
    {
      key: "temperature_tracking",
      label: "Temperature Tracking",
      type: "boolean",
      default: true,
      description: "Track temperature derived from climate, season, weather, and time of day",
      condition: { key: "contextual_environment", value: false },
    },
    {
      key: "turn_mode",
      label: "Turn Mode",
      type: "select",
      options: [
        { value: "free_form", label: "Free-form (no enforced order)" },
        { value: "round_robin", label: "Round Robin (fixed rotation)" },
        { value: "initiative_order", label: "Initiative (rolled order)" },
        { value: "concurrent", label: "Concurrent (all act simultaneously)" },
      ],
      default: "free_form",
      description: "Optional turn-flow structure layered onto the time system",
    },
    {
      key: "phases_per_turn",
      label: "Structured Phases",
      type: "boolean",
      default: false,
      description: "Each turn follows Action → Resolution → Narration phases",
    },
    {
      key: "mandatory_dice",
      label: "Mandatory Dice for All Actions",
      type: "boolean",
      default: false,
      description: "Every meaningful action requires a dice roll",
    },
    {
      key: "time_limit_seconds",
      label: "Turn Time Limit (seconds)",
      type: "number",
      default: 0,
      description: "Real-time limit per turn (0 = unlimited)",
    },
  ],
  systemPromptSection: (config) => {
    const contextual = config.contextual_environment === true;
    const turnMode = config.turn_mode || "free_form";
    const phases = config.phases_per_turn === true;
    const mandatoryDice = config.mandatory_dice === true;
    const timeLimit = config.time_limit_seconds || 0;

    const turnModeDesc: Record<string, string> = {
      free_form: "Players act freely in any order. No turn enforcement.",
      round_robin: "Players take turns in fixed rotation. Use end_turn after each consequential action resolves.",
      initiative_order: "At the start of encounters, use set_turn_order to roll initiative and resolve actions in that order.",
      concurrent: "All players may act simultaneously. Resolve actions in the order received.",
    };

    const turnFlowSection = turnMode !== "free_form" || phases || mandatoryDice || timeLimit > 0
      ? `\n### TURN FLOW\n- Turn mode: ${turnMode}. ${turnModeDesc[turnMode]}\n${phases ? "- TURN PHASES: Each turn follows Action → Resolution → Narration." : ""}\n${mandatoryDice ? "- MANDATORY DICE: Every meaningful player action requires a roll." : ""}\n${timeLimit > 0 ? `- TIME LIMIT: ${timeLimit} seconds per turn.` : ""}\n- Track \"current_turn_player\", \"turn_order\", and \"current_turn_index\" when structured turn flow is active.`
      : "";

    if (contextual) {
      return `## TIME PASSAGE (Contextual / AI-Declared)
- You manage time, weather, and environment NARRATIVELY — no complex hour calculations required.
- Use set_environment tool to declare the current state whenever it changes or matters to the story.
- State variables available: "day_phase", "current_weather", "current_season", "current_temperature", "environment_description", "calendar_date", "game_day".
- Declare these as the narrative demands:
  • day_phase: dawn, morning, afternoon, evening, night — set when time of day changes or matters.
  • current_weather: clear, cloudy, rain, storm, snow, fog, etc. — set when weather changes or is relevant.
  • current_season: spring, summer, autumn, winter — set at story start and when it changes.
  • current_temperature: approximate °C or a descriptor like "freezing", "hot" — set when relevant.
  • environment_description: freeform text describing the ambient environment (e.g., "A cold wind howls through the mountain pass").
  • calendar_date: in-world date string (e.g., "3rd of Frostmoon", "March 15, 44 BC", "Stardate 47634.2").
  • game_day: increment when a new day begins.

### GUIDELINES
- Don't calculate precise hours — just declare what phase of day it is.
- Weather and temperature are narrative tools, not simulations. Use them for atmosphere and stakes.
- Let the story dictate when environment changes matter. A calm conversation doesn't need weather updates.
- Extreme conditions (blizzard, heatwave, supernatural weather) should create narrative consequences.
- NPCs and the world react to environment: shops close at night, farmers work in daylight, storms delay travel.${turnFlowSection}`;
    }

    const mode = config.mode || "turns";
    const hpt = config.hours_per_turn || 1;
    const weather = config.weather_enabled !== false;
    const climate = config.climate || "temperate";
    const seasons = config.seasons_enabled !== false;
    const tempTracking = config.temperature_tracking !== false;

    const modeDesc: Record<string, string> = {
      turns: `Each player turn advances the clock by ${hpt} hour(s). Use advance_time after each player action.`,
      realtime: `Time maps to real wall clock (configurable ratio). The clock ticks independently.`,
      hybrid: `Player actions advance time by ${hpt}h. Between turns, time also ticks slowly.`,
    };

    return `## TIME PASSAGE (Rules — Core Clock)
- Mode: ${mode}. ${modeDesc[mode]}
- State variables: "game_hour" (0-23), "game_day" (1+), "day_phase" (dawn/morning/afternoon/evening/night).
- Day phases auto-derived from hour and season (seasons shift dawn/dusk times).
- Use advance_time tool to move time forward. Day phase updates automatically.
- DAY/NIGHT EFFECTS:
  - Night: reduced visibility, different creatures, closed shops, stealth advantage.
  - Dawn/dusk: transition periods with unique encounters.
  - NPCs follow schedules based on time.
${seasons ? `- SEASONS: Track "current_season" (spring/summer/autumn/winter). Changes every ${config.days_per_season || 30} days.
  - Seasons affect weather probability, day length, and temperature.
  - Spring: renewal, planting. Summer: peak activity, long days. Autumn: harvest, shortening days. Winter: harsh, short days.` : "- Seasons: disabled."}
${weather ? `- WEATHER (climate: ${climate}):
  - Weather is ALGORITHMIC — transitions happen automatically via postTurnHook based on climate-specific weather tables.
  - Track "weather_state" object: { condition, intensity, duration_remaining }.
  - Weather affects gameplay: rain→slippery/-2 DEX checks, storms→low visibility/-2 perception, snow→slow travel.
  - Severe weather: disadvantage on ranged attacks, +50% travel time.
  - Use set_weather tool ONLY for narrative overrides (magical fog, dramatic storm). Normal weather auto-transitions.
  - Describe weather atmospherically. Extreme weather creates hazards or opportunities.` : "- Weather: disabled."}
${tempTracking ? `- TEMPERATURE: Tracked as "current_temperature" (°C), derived from climate + season + weather + time of day.
  - Extreme cold (<-10°C): survival needs deplete faster, CON checks to avoid frostbite.
  - Extreme heat (>40°C): exhaustion risk, thirst depletes faster.
  - Emits temperature_extreme events for survival mechanic cross-reactions.` : ""}
- REST SYSTEM: Use the rest tool for structured resting.
  - short_rest (1h): minor recovery, catch breath.
  - long_rest (8h): full recovery, heal HP, restore spell slots, satisfy rest need.
  - camp (custom hours): flexible rest with time advancement.
  - Rest emits rest_completed event for cross-mechanic reactions (combat healing, survival restoration, magic recovery).
- Other mechanics that depend on time reference this clock.${turnFlowSection}`;
  },
  widgets: [
    {
      id: "time_status",
      type: "status_bar_item",
      label: "Time",
      icon: "Clock",
      component: "TimeStatus",
      dataBindings: {
        hour: "variables.game_hour",
        day: "variables.game_day",
        weather: "variables.weather_state",
        dayPhase: "variables.day_phase",
        season: "variables.current_season",
        temperature: "variables.current_temperature",
        environmentDescription: "variables.environment_description",
        calendarDate: "variables.calendar_date",
      },
      visibility: "when_active",
      priority: 1,
    },
    {
      id: "initiative_order",
      component: "InitiativeOrderWidget",
      label: "Initiative",
      icon: "ListOrdered",
      type: "sidebar_tab",
      dataBindings: {
        turnOrder: "variables.turn_order",
        currentPlayer: "variables.current_turn_player",
        currentIndex: "variables.current_turn_index",
      },
      visibility: "when_data",
      priority: 5,
    },
  ],
  initializeState: (config) => {
    const contextual = config.contextual_environment === true;

    if (contextual) {
      return {
        game_hour: undefined,
        game_day: 1,
        day_phase: "morning",
        weather_state: undefined,
        current_weather: undefined,
        current_season: undefined,
        current_temperature: undefined,
        environment_description: "",
        calendar_date: "",
        turn_order: [],
        current_turn_player: null,
        current_turn_index: 0,
        _turn_mode: config.turn_mode || "free_form",
        _current_phase: null,
        _last_time_advance: null,
      };
    }

    const startHour = config.start_hour || 8;
    const climate = config.climate || "temperate";
    const seasons = config.seasons_enabled !== false;
    const weather = config.weather_enabled !== false;
    const tempTracking = config.temperature_tracking !== false;
    const season = seasons ? "spring" : undefined;

    const initialWeather = weather ? { condition: "clear", intensity: "light", duration_remaining: 4 } : undefined;
    const baseTemp = season ? BASE_TEMP[climate]?.[season] : BASE_TEMP[climate]?.spring;
    const isDay = startHour >= 6 && startHour < 18;
    const temp = tempTracking && baseTemp ? (isDay ? baseTemp.day : baseTemp.night) : undefined;

    return {
      game_hour: startHour,
      game_day: 1,
      day_phase: "morning",
      weather_state: initialWeather,
      current_weather: initialWeather?.condition || (weather ? "clear" : undefined),
      current_season: season,
      current_temperature: temp,
      environment_description: "",
      calendar_date: "",
      turn_order: [],
      current_turn_player: null,
      current_turn_index: 0,
      _turn_mode: config.turn_mode || "free_form",
      _current_phase: null,
      _last_time_advance: null,
    };
  },
  onEvent: (event) => {
    if (event.type === "weather_changed") {
      const condition = event.data.condition;
      const notes: string[] = [];
      if (condition === "storm" || condition === "blizzard" || condition === "ion_storm") {
        notes.push("⛈ Severe weather: -2 ranged attacks, -2 perception, +50% travel time");
      } else if (condition === "rain" || condition === "snow" || condition === "acid_rain") {
        notes.push(`🌧 ${condition}: -2 DEX-based checks, slippery surfaces`);
      } else if (condition === "fog" || condition === "spore_fog") {
        notes.push("🌫 Low visibility: -2 perception, stealth advantage");
      }
      return notes.length > 0 ? { narrativeNotes: notes } : null;
    }
    return null;
  },
  postTurnHook: (context) => {
    const config = context.config;

    // In contextual mode, no automatic weather calculations
    if (config.contextual_environment === true) return null;

    if (config.weather_enabled === false) return null;

    const weatherState = context.state.variables?.weather_state;
    if (!weatherState) return null;

    const updates: Record<string, any> = {};
    const notes: string[] = [];
    const events: any[] = [];

    // Decrement weather duration
    const remaining = (weatherState.duration_remaining || 1) - 1;
    if (remaining <= 0) {
      const climate = config.climate || "temperate";
      const season = context.state.variables?.current_season || "spring";
      const table = WEATHER_TABLES[climate]?.[season]?.conditions || WEATHER_TABLES.temperate.spring.conditions;

      const entries = table.map(e => {
        const [cond, w] = e.split(":");
        return { condition: cond, weight: parseInt(w) };
      });
      const totalWeight = entries.reduce((s, e) => s + e.weight, 0);
      let roll = Math.random() * totalWeight;
      let newCondition = entries[0].condition;
      for (const entry of entries) {
        roll -= entry.weight;
        if (roll <= 0) { newCondition = entry.condition; break; }
      }

      const intensities = ["light", "moderate", "severe"];
      const newIntensity = intensities[Math.floor(Math.random() * 3)];
      const newDuration = 2 + Math.floor(Math.random() * 7);

      const newWeatherState = { condition: newCondition, intensity: newIntensity, duration_remaining: newDuration };
      updates.weather_state = newWeatherState;
      updates.current_weather = newCondition;

      if (newCondition !== weatherState.condition) {
        notes.push(`🌤 Weather changed: ${weatherState.condition} → ${newCondition} (${newIntensity})`);
        events.push({ type: "weather_changed", source: "time_passage", data: { condition: newCondition, intensity: newIntensity, previous: weatherState.condition } });
      }
    } else {
      updates.weather_state = { ...weatherState, duration_remaining: remaining };
    }

    // Update temperature
    if (config.temperature_tracking !== false) {
      const climate = config.climate || "temperate";
      const season = context.state.variables?.current_season || "spring";
      const hour = context.state.variables?.game_hour ?? 8;
      const baseTemps = BASE_TEMP[climate]?.[season] || BASE_TEMP.temperate.spring;
      const isDay = hour >= 6 && hour < 18;
      const baseTemp = isDay ? baseTemps.day : baseTemps.night;
      const weatherMod = WEATHER_TEMP_MOD[updates.current_weather || context.state.variables?.current_weather || "clear"] || 0;
      const temp = baseTemp + weatherMod;
      updates.current_temperature = temp;

      if (temp < -10 || temp > 40) {
        events.push({ type: "temperature_extreme", source: "time_passage", data: { temperature: temp, extreme: temp < -10 ? "cold" : "heat" } });
        notes.push(`🌡 Extreme ${temp < -10 ? "cold" : "heat"}: ${temp}°C — survival hazard!`);
      }
    }

    // Season check
    if (config.seasons_enabled !== false) {
      const daysPerSeason = config.days_per_season || 30;
      const gameDay = context.state.variables?.game_day || 1;
      const seasons = ["spring", "summer", "autumn", "winter"];
      const seasonIndex = Math.floor((gameDay - 1) / daysPerSeason) % 4;
      const newSeason = seasons[seasonIndex];
      if (newSeason !== context.state.variables?.current_season) {
        updates.current_season = newSeason;
        notes.push(`🍂 Season changed to ${newSeason}!`);
      }
    }

    return { stateUpdates: updates, narrativeNotes: notes.length > 0 ? notes : undefined };
  },
  tools: [
    {
      type: "function",
      function: {
        name: "advance_time",
        description: "Advance the game clock by a specified number of hours. Updates day_phase, weather, temperature, and triggers time-based effects.",
        parameters: {
          type: "object",
          properties: {
            hours: { type: "number", description: "Number of hours to advance" },
            reason: { type: "string", description: "Why time is advancing (travel, rest, activity)" },
          },
          required: ["hours", "reason"],
        },
      },
    },
    {
      type: "function",
      function: {
        name: "set_weather",
        description: "Override current weather for narrative purposes (magical fog, dramatic storm). Normal weather transitions happen automatically.",
        parameters: {
          type: "object",
          properties: {
            condition: { type: "string", description: "Weather condition (clear, rain, storm, snow, fog, blizzard, sandstorm, etc.)" },
            intensity: { type: "string", enum: ["light", "moderate", "severe"], description: "How intense the weather is" },
            duration_hours: { type: "number", description: "How many hours this weather lasts before normal transitions resume" },
          },
          required: ["condition", "intensity", "duration_hours"],
        },
      },
    },
    {
      type: "function",
      function: {
        name: "rest",
        description: "Player rests. Advances time, heals HP, restores survival needs, recovers spell slots. Emits rest_completed event.",
        parameters: {
          type: "object",
          properties: {
            type: { type: "string", enum: ["short_rest", "long_rest", "camp"], description: "Rest type: short (1h), long (8h), camp (custom)" },
            hours: { type: "number", description: "Custom hours for camp type (ignored for short/long)" },
            narrative: { type: "string", description: "What happens during the rest" },
          },
          required: ["type"],
        },
      },
    },
    {
      type: "function",
      function: {
        name: "end_turn",
        description: "End the current player's turn and advance to the next player in the turn order",
        parameters: {
          type: "object",
          properties: {
            current_player: { type: "string", description: "Name of the player whose turn just ended" },
            summary: { type: "string", description: "Brief summary of what happened this turn" },
          },
          required: ["current_player"],
        },
      },
    },
    {
      type: "function",
      function: {
        name: "set_turn_order",
        description: "Set the turn order for all participants (used for initiative-based turns)",
        parameters: {
          type: "object",
          properties: {
            order: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  name: { type: "string" },
                  initiative: { type: "number" },
                },
                required: ["name", "initiative"],
              },
              description: "Participants sorted by initiative (highest first)",
            },
          },
          required: ["order"],
        },
      },
    },
    {
      type: "function",
      function: {
        name: "set_environment",
        description: "Declare the current time of day, weather, season, temperature, date, or ambient description. Use whenever the environment changes or is narratively relevant. Works in both contextual and algorithmic modes.",
        parameters: {
          type: "object",
          properties: {
            day_phase: { type: "string", enum: ["dawn", "morning", "afternoon", "evening", "night"], description: "Current time of day" },
            weather: { type: "string", description: "Current weather (clear, cloudy, rain, storm, snow, fog, etc.)" },
            season: { type: "string", enum: ["spring", "summer", "autumn", "winter"], description: "Current season" },
            temperature: { type: "string", description: "Temperature as number (°C) or descriptor (freezing, cold, mild, warm, hot, scorching)" },
            calendar_date: { type: "string", description: "In-world date (e.g., '3rd of Frostmoon', 'March 15', 'Stardate 47634.2')" },
            environment_description: { type: "string", description: "Freeform ambient description of the environment" },
            game_day: { type: "number", description: "Set the current day number" },
          },
          required: [],
        },
      },
    },
  ],
};
